import { useEffect, useMemo, useState } from "react";
import { getBasketCheckoutUrgency, type BasketCheckoutUrgencyState } from "../../../functions/src/common/checkoutDeadlineUrgency";
import type { Language } from "../../api/langConfig";
import { useOrderDayDeadlineContext } from "../useOrderDayDeadlineContext";
import type { BasketEntry } from "./useBasket";

export type BasketCheckoutUrgencyHookState = {
    checkoutUrgency: BasketCheckoutUrgencyState;
    checkoutUrgencyDismissed: boolean;
    dismissCheckoutUrgency: () => void;
};

/** Urgency banner — uses {@link basketServerNow} from {@link useBasketEntries} (shared deadline clock). */
export function useBasketCheckoutUrgency(
    entries: BasketEntry[],
    entriesLoading: boolean,
    lang: Language,
    basketServerNow: Date | undefined,
): BasketCheckoutUrgencyHookState {
    const deadlineCtx = useOrderDayDeadlineContext();
    const [checkoutUrgencyDismissed, setCheckoutUrgencyDismissed] = useState(false);

    const entryInputs = useMemo(
        () => entries.map(e => ({ date: e.date, draft: e.draft, placed: e.placed })),
        [entries],
    );

    const checkoutUrgency = useMemo((): BasketCheckoutUrgencyState => {
        if (!basketServerNow || !deadlineCtx || entriesLoading) {
            return { active: false };
        }
        return getBasketCheckoutUrgency(entryInputs, deadlineCtx, basketServerNow, lang);
    }, [basketServerNow, deadlineCtx, entriesLoading, entryInputs, lang]);

    useEffect(() => {
        if (!checkoutUrgency.active) {
            setCheckoutUrgencyDismissed(false);
        }
    }, [checkoutUrgency.active]);

    const dismissCheckoutUrgency = () => setCheckoutUrgencyDismissed(true);

    return { checkoutUrgency, checkoutUrgencyDismissed, dismissCheckoutUrgency };
}
